import React, { useEffect, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { instance } from '../../axios';
import strings from '../../i18n/language';

const BRAND = '#3b95e8';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.45)';

// Monday of the week holding that day (dayjs: 0 = Sunday)
const mondayOf = (day) => day.startOf('day').subtract((day.day() + 6) % 7, 'day');

const dayLabel = new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short' });

const NavButton = ({ title, disabled, onClick, children }) => (
  <Tooltip title={title} placement="bottom">
    <span>
      <IconButton size="small" disabled={disabled} onClick={onClick} sx={{
        color: '#e8f0fe',
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
      }}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

// A shop's weekly report as mailed on Sunday evening, any week: fetched with
// the Authorization header (a URL would need ?token=, which the API logs)
const CompanyReport = ({ company, onClose }) => {
  const [week, setWeek] = useState(() => mondayOf(dayjs()));
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (company) setWeek(mondayOf(dayjs()));
  }, [company]);

  useEffect(() => {
    if (!company) return undefined;
    let cancelled = false;
    setHtml(null);
    setError(false);
    instance
      .get(`/company/${company.id}/report`, { params: { week: week.format('YYYY-MM-DD') } })
      .then((response) => { if (!cancelled) setHtml(response.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [company, week]);

  const isCurrentWeek = week.isSame(mondayOf(dayjs()), 'day');
  const label = `${dayLabel.format(week.toDate())} – ${dayLabel.format(week.add(6, 'day').toDate())}`;

  return (
    <Dialog
      open={Boolean(company)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { background: '#0d1b36', border: `1px solid ${BORDER}`, height: '90vh', overflow: 'hidden' } }}
    >
      {/* Header: shop and week selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', flex: 1 }} noWrap>
          {strings.LABEL_COMPANIES_REPORT} · {company?.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NavButton title={strings.LABEL_COMPANIES_REPORT_PREVIOUS_WEEK} onClick={() => setWeek(week.subtract(7, 'day'))}>
            <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
          </NavButton>
          <Box sx={{ minWidth: 150, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#e8f0fe' }}>{label}</Typography>
            {isCurrentWeek && (
              <Typography sx={{ fontSize: 10, color: BRAND, letterSpacing: 0.4 }}>
                {strings.LABEL_COMPANIES_REPORT_CURRENT_WEEK}
              </Typography>
            )}
          </Box>
          <NavButton title={strings.LABEL_COMPANIES_REPORT_NEXT_WEEK} disabled={isCurrentWeek} onClick={() => setWeek(week.add(7, 'day'))}>
            <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
          </NavButton>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: MUTED }}>
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Report */}
      <Box sx={{ flex: 1, position: 'relative', background: '#0a1628' }}>
        {!html && !error && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ color: BRAND }} />
          </Box>
        )}
        {error && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#ff7b7b' }}>{strings.LABEL_COMPANIES_REPORT_ERROR}</Typography>
          </Box>
        )}
        {html && (
          <iframe
            title={strings.LABEL_COMPANIES_REPORT}
            srcDoc={html}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default CompanyReport;
